import { useEffect, useMemo, useState, type ReactElement } from 'react'
import {
  ArrowLeft, ArrowRight, Bell, BellRing, Check, ChevronLeft, FlaskConical,
  Mail, Megaphone, Pencil, Search, Smartphone, Undo2,
} from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Select, ToastProvider, useToast, apiGet, adminMutate, usePermissions } from './primitives.js'
import { Field, TextInput, NumberInput, SwitchRow, ChannelCard, SentenceToken } from './form.js'
import { useSaveBar } from '../shell/editing.js'
import { navigate } from '../router.js'
import { iconOf } from '../hubs/InfraCatalog.js'

/** Pastilles des valeurs d'état (jamais la couleur seule : le libellé prime). */
const STATE_DOTS: Record<string, string> = {
  HEALTHY: 'lc-dot--ok', DEGRADED: 'lc-dot--warning', UNAVAILABLE: 'lc-dot--critical',
  STALE: 'lc-dot--idle', UNKNOWN: 'lc-dot--idle',
  ONLINE: 'lc-dot--ok', WARNING: 'lc-dot--warning', OFFLINE: 'lc-dot--critical', MISSING: 'lc-dot--idle',
}
const CHANNEL_ICONS: Record<string, ReactElement> = {
  IN_APP: <Bell size={17} />, EMAIL: <Mail size={17} />, PUSH: <Smartphone size={17} />,
}

/**
 * RULE BUILDER — WIZARD (LOT 9.0.1) — /alerts/rules/new et /alerts/rules/:id.
 *
 * Refonte UX du parcours LOT 9 : UNE étape à la fois, dans le viewport,
 * avec un rail de progression cliquable, une navigation Précédent/Suivant,
 * une VALIDATION HUMAINE par étape et un RÉSUMÉ VIVANT qui raconte la règle
 * en français au fil de la saisie. Le moteur métier LOT 9 est INCHANGÉ :
 * même brouillon, même payload, même API, même Save Bar (la création passe
 * par elle — aucun bouton « Enregistrer » isolé).
 *
 * Toutes les listes viennent de /alert-rules/options (Metric Catalog) —
 * rien n'est codé en dur ici. La simulation reste un dry-run serveur.
 */

interface MetricOption {
  key: string; label: string; type: string; unit: string | null
  technical_key: string; operators: string[]
  enum_options?: { value: string; label: string }[]
  components?: { key: string; name: string }[]
}
interface ServiceOption {
  slug: string; name: string; category: string; category_kind: string
  prominence: string; icon: string | null; state: string; is_enabled: boolean
  metrics: MetricOption[]
}
interface Options {
  services: ServiceOption[]
  severities: { value: string; label: string }[]
  channels: { value: string; label: string; available: boolean; note: string | null }[]
  recipients: {
    users: { id: string; display_name: string }[]
    roles: { key: string; label: string }[]
    groups: { id: string; name: string; members: number }[]
  }
}

interface Draft {
  name: string
  description: string
  service_slug: string | null
  component_key: string | null
  metric_key: string | null
  operator: string | null
  threshold: string           // saisie brute — convertie au moment d'envoyer
  duration_value: number
  duration_unit: 's' | 'min' | 'h'
  severity: string
  recipients: { kind: 'USER' | 'ROLE' | 'GROUP'; id: string }[]
  channels_fire: string[]
  auto_resolve: boolean
  notify_recovery: boolean
  channels_recovery: string[]
  cooldown_value: number
  cooldown_unit: 's' | 'min'
  advanced_recovery: boolean
  recovery_operator: string | null
  recovery_threshold: string
  title_override: string
  email_subject_override: string
}

const EMPTY: Draft = {
  name: '', description: '', service_slug: null, component_key: null,
  metric_key: null, operator: null, threshold: '',
  duration_value: 2, duration_unit: 'min', severity: 'WARNING',
  recipients: [], channels_fire: ['IN_APP'],
  auto_resolve: true, notify_recovery: true, channels_recovery: ['IN_APP'],
  cooldown_value: 2, cooldown_unit: 'min',
  advanced_recovery: false, recovery_operator: null, recovery_threshold: '',
  title_override: '', email_subject_override: '',
}

const OP_LABELS: Record<string, string> = {
  GT: 'supérieur à', GTE: 'supérieur ou égal à', LT: 'inférieur à',
  LTE: 'inférieur ou égal à', EQ: 'est égal à', NEQ: 'est différent de',
  IS_TRUE: 'est vrai', IS_FALSE: 'est faux',
}
const opLabel = (type: string, op: string): string =>
  type === 'STATE' ? (op === 'EQ' ? 'est' : op === 'NEQ' ? 'n’est pas' : OP_LABELS[op] ?? op) : OP_LABELS[op] ?? op

const toSeconds = (v: number, u: 's' | 'min' | 'h'): number => u === 'h' ? v * 3600 : u === 'min' ? v * 60 : v
const fromSeconds = (s: number): { v: number; u: 's' | 'min' | 'h' } =>
  s % 3600 === 0 && s > 0 ? { v: s / 3600, u: 'h' } : s % 60 === 0 && s > 0 ? { v: s / 60, u: 'min' } : { v: s, u: 's' }

const durHuman = (v: number, u: 's' | 'min' | 'h'): string => {
  if (v <= 0) return 'immédiatement'
  const unit = u === 'min' ? `minute${v > 1 ? 's' : ''}` : u === 'h' ? `heure${v > 1 ? 's' : ''}` : `seconde${v > 1 ? 's' : ''}`
  return `${v} ${unit}`
}

/* ═══════════ Découpage du wizard ═══════════ */

interface StepDef { key: string; label: string; ask: string; hint?: string }

const STEPS: StepDef[] = [
  { key: 'source', label: 'Source', ask: 'Que voulez-vous surveiller ?', hint: 'Tous les services du catalogue — un service masqué du hub reste surveillable.' },
  { key: 'metric', label: 'Donnée', ask: 'Quelle donnée de ce service ?', hint: 'Les données réellement observées, avec leurs libellés humains.' },
  { key: 'condition', label: 'Condition', ask: 'Quand devient-elle importante ?', hint: 'Vous pouvez tester la condition avec la valeur actuelle, sans rien déclencher.' },
  { key: 'timing', label: 'Temporisation', ask: 'Combien de temps avant d’alerter ?', hint: 'Un pic de quelques secondes ne réveille personne — la condition doit tenir.' },
  { key: 'alert', label: 'Alerte', ask: 'Quelle alerte créer ?' },
  { key: 'who', label: 'Destinataires', ask: 'Qui doit être prévenu ?', hint: 'Une personne ciblée plusieurs fois (rôle + groupe) ne reçoit qu’un message par canal.' },
  { key: 'channels', label: 'Communications', ask: 'Par quels canaux ?', hint: 'La politique d’organisation et les préférences personnelles s’appliquent ensuite.' },
  { key: 'recap', label: 'Récapitulatif', ask: 'Tout est prêt ?' },
]

const DURATION_PRESETS: { label: string; v: number; u: Draft['duration_unit'] }[] = [
  { label: 'Immédiat', v: 0, u: 's' }, { label: '30 s', v: 30, u: 's' },
  { label: '1 min', v: 1, u: 'min' }, { label: '2 min', v: 2, u: 'min' },
  { label: '5 min', v: 5, u: 'min' }, { label: '15 min', v: 15, u: 'min' },
  { label: '1 h', v: 1, u: 'h' },
]
const COOLDOWN_PRESETS: { label: string; v: number; u: Draft['cooldown_unit'] }[] = [
  { label: 'Aucun', v: 0, u: 's' }, { label: '1 min', v: 1, u: 'min' },
  { label: '2 min', v: 2, u: 'min' }, { label: '5 min', v: 5, u: 'min' },
  { label: '15 min', v: 15, u: 'min' },
]

const SEV_META: Record<string, { dot: string; desc: string }> = {
  INFO: { dot: 'lc-dot--info', desc: 'Bon à savoir — n’exige pas d’action.' },
  WARNING: { dot: 'lc-dot--warning', desc: 'Quelque chose mérite un œil rapidement.' },
  CRITICAL: { dot: 'lc-dot--critical', desc: 'Le club est impacté — il faut agir.' },
}

function Inner({ ruleId }: { ruleId: string | null }) {
  const toast = useToast()
  const { permissions } = usePermissions()
  const isSuperAdmin = permissions.includes('system.safe_mode')
  const [options, setOptions] = useState<Options | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [initial, setInitial] = useState<Draft>(EMPTY)
  const [step, setStep] = useState(ruleId ? STEPS.length - 1 : 0)
  const [visited, setVisited] = useState(ruleId ? STEPS.length - 1 : 0)
  const [search, setSearch] = useState('')
  const [sim, setSim] = useState<{ current_value: string; verdict: string } | null>(null)
  const [showOverrides, setShowOverrides] = useState(false)
  const [loaded, setLoaded] = useState(ruleId === null)

  useEffect(() => {
    apiGet<Options>('/api/v1/alert-rules/options').then(setOptions).catch(() => undefined)
  }, [])

  /* Édition : hydrate le brouillon depuis la règle existante. */
  useEffect(() => {
    if (!ruleId) return
    apiGet<Record<string, unknown>>(`/api/v1/alert-rules/${ruleId}`).then((r) => {
      const dur = fromSeconds(Number(r['duration_s'] ?? 0))
      const cd = fromSeconds(Number(r['cooldown_s'] ?? 0))
      const d: Draft = {
        name: String(r['name'] ?? ''), description: String(r['description'] ?? '') || '',
        service_slug: (r['service_slug'] as string) ?? null,
        component_key: (r['component_key'] as string | null) ?? null,
        metric_key: (r['metric_key'] as string) ?? null,
        operator: (r['operator'] as string) ?? null,
        threshold: r['threshold'] === null || r['threshold'] === undefined ? '' : String(r['threshold']),
        duration_value: dur.v, duration_unit: dur.u,
        severity: String(r['severity'] ?? 'WARNING'),
        recipients: (r['recipients'] as Draft['recipients']) ?? [],
        channels_fire: (r['channels_fire'] as string[]) ?? ['IN_APP'],
        auto_resolve: Boolean(r['auto_resolve']),
        notify_recovery: Boolean(r['notify_recovery']),
        channels_recovery: (r['channels_recovery'] as string[]) ?? ['IN_APP'],
        cooldown_value: cd.u === 'h' ? cd.v * 60 : cd.v, cooldown_unit: cd.u === 's' ? 's' : 'min',
        advanced_recovery: r['recovery_operator'] !== null && r['recovery_operator'] !== undefined,
        recovery_operator: (r['recovery_operator'] as string | null) ?? null,
        recovery_threshold: r['recovery_threshold'] === null || r['recovery_threshold'] === undefined ? '' : String(r['recovery_threshold']),
        title_override: String(r['title_override'] ?? '') || '',
        email_subject_override: String(r['email_subject_override'] ?? '') || '',
      }
      setDraft(d); setInitial(d); setLoaded(true)
      if (d.title_override || d.email_subject_override || d.description) setShowOverrides(true)
    }).catch(() => undefined)
  }, [ruleId])

  const upd = (p: Partial<Draft>) => { setDraft((d) => ({ ...d, ...p })); setSim(null) }

  const service = useMemo(() => options?.services.find((s) => s.slug === draft.service_slug) ?? null, [options, draft.service_slug])
  const metric = useMemo(() => service?.metrics.find((m) => m.key === draft.metric_key) ?? null, [service, draft.metric_key])

  const parsedThreshold = (raw: string): number | string | null => {
    if (!metric) return null
    if (metric.type === 'STATE') return raw || null
    if (metric.type === 'BOOLEAN') return null
    if (metric.type === 'AGE') return raw === '' ? null : toSeconds(Number(raw), 'min')
    return raw === '' ? null : Number(raw)
  }

  /* ─── Validation PAR ÉTAPE : un booléen + un message humain ciblé. ─── */
  const stepIssue: (string | null)[] = [
    draft.service_slug ? null : 'Choisissez le service à surveiller.',
    !draft.metric_key ? 'Choisissez une donnée à surveiller.'
      : draft.metric_key === 'component.status' && !draft.component_key ? 'Choisissez le composant concerné.' : null,
    !draft.operator ? 'Choisissez un opérateur.'
      : metric?.type !== 'BOOLEAN' && draft.threshold === ''
        ? (metric?.type === 'STATE' ? 'Choisissez un état.' : 'Indiquez une valeur.') : null,
    null,
    draft.name.trim().length > 0 ? null : 'Donnez un nom à la règle.',
    draft.recipients.length > 0 ? null : 'Ajoutez au moins un destinataire (personne, rôle ou groupe).',
    draft.channels_fire.length > 0 ? null : 'Choisissez au moins un canal de déclenchement.',
    null,
  ]
  const stepDone = stepIssue.map((x) => x === null)
  const firstIssue = stepIssue.findIndex((x) => x !== null)
  const allValid = firstIssue === -1
  /* Une étape est atteignable si toutes celles d'avant sont valides — ou si
     on l'a déjà visitée (on peut toujours REVENIR corriger). */
  const reachable = (i: number) => i <= visited || stepIssue.slice(0, i).every((x) => x === null)
  const goto = (i: number) => {
    if (!reachable(i)) return
    setStep(i)
    setVisited((v) => Math.max(v, i))
  }
  /* Avance directe après un choix qui VIENT de valider l'étape (le state
     React n'est pas encore recalculé — pas de garde reachable ici). */
  const advance = (i: number) => {
    setStep(i)
    setVisited((v) => Math.max(v, i))
  }

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial])

  const payload = () => ({
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    service_slug: draft.service_slug,
    component_key: draft.metric_key === 'component.status' ? draft.component_key : null,
    metric_key: draft.metric_key,
    operator: draft.operator,
    threshold: metric?.type === 'BOOLEAN' ? null : parsedThreshold(draft.threshold),
    duration_s: Math.min(86400, Math.max(0, toSeconds(draft.duration_value, draft.duration_unit))),
    severity: draft.severity,
    auto_resolve: draft.auto_resolve,
    recovery_operator: draft.advanced_recovery ? draft.recovery_operator : null,
    recovery_threshold: draft.advanced_recovery ? parsedThreshold(draft.recovery_threshold) : null,
    cooldown_s: Math.min(86400, Math.max(0, toSeconds(draft.cooldown_value, draft.cooldown_unit))),
    recipients: draft.recipients,
    channels_fire: draft.channels_fire,
    notify_recovery: draft.notify_recovery,
    channels_recovery: draft.channels_recovery,
    title_override: draft.title_override.trim() || null,
    message_override: null,
    email_subject_override: draft.email_subject_override.trim() || null,
  })

  useSaveBar({
    dirty: dirty && loaded,
    saveLabel: ruleId ? 'Enregistrer' : 'Créer la règle',
    invalid: allValid ? null : stepIssue[firstIssue] ?? 'Complétez les étapes avant de créer la règle.',
    onSave: async () => {
      const r = ruleId
        ? await adminMutate<{ error?: string; message?: string }>('PUT', `/api/v1/alert-rules/${ruleId}`, payload())
        : await adminMutate<{ id?: string; error?: string; message?: string }>('POST', '/api/v1/alert-rules', payload())
      if (r.status < 300) {
        setInitial(draft)
        setTimeout(() => navigate('/alerts/rules'), 350)
        return true
      }
      return (r.data as { message?: string }).message ?? 'L’enregistrement a échoué — rien n’a changé.'
    },
    onCancel: () => { setDraft(initial); setSim(null) },
    successMessage: ruleId ? 'Règle mise à jour.' : 'Règle créée et active.',
  })

  const simulate = async () => {
    const r = await adminMutate<{ error?: string; current_value?: string; verdict?: string; message?: string }>('POST', '/api/v1/alert-rules/simulate', {
      service_slug: draft.service_slug, component_key: draft.metric_key === 'component.status' ? draft.component_key : null,
      metric_key: draft.metric_key, operator: draft.operator,
      threshold: metric?.type === 'BOOLEAN' ? null : parsedThreshold(draft.threshold),
      duration_s: toSeconds(draft.duration_value, draft.duration_unit),
    })
    if (r.status < 300 && r.data.verdict) setSim({ current_value: r.data.current_value ?? '', verdict: r.data.verdict })
    else toast('error', r.data.message ?? 'Simulation impossible.')
  }

  /* ─── Les morceaux du résumé vivant (français naturel, jamais de jargon). ─── */
  const summary = useMemo(() => {
    const compName = draft.metric_key === 'component.status'
      ? metric?.components?.find((c) => c.key === draft.component_key)?.name ?? null : null
    const valLabel = !metric ? null
      : metric.type === 'STATE'
        ? metric.enum_options?.find((o) => o.value === draft.threshold)?.label ?? (draft.threshold || null)
        : metric.type === 'BOOLEAN' ? ''
        : draft.threshold === '' ? null
        : metric.type === 'AGE' ? `${draft.threshold} min`
        : `${draft.threshold}${metric.unit ? ` ${metric.unit}` : ''}`
    const condition = service && metric && draft.operator && valLabel !== null
      ? `${compName ? `${compName} (${service.name})` : service.name} → ${metric.label.toLowerCase()} ${opLabel(metric.type, draft.operator)}${valLabel ? ` ${valLabel}` : ''}`
      : null
    const durS = toSeconds(draft.duration_value, draft.duration_unit)
    const who = draft.recipients.map((r) =>
      r.kind === 'GROUP' ? options?.recipients.groups.find((g) => g.id === r.id)?.name
        : r.kind === 'ROLE' ? `rôle ${options?.recipients.roles.find((x) => x.key === r.id)?.label ?? r.id}`
        : options?.recipients.users.find((u) => u.id === r.id)?.display_name).filter((x): x is string => Boolean(x))
    const chans = draft.channels_fire.map((c) => options?.channels.find((x) => x.value === c)?.label ?? c)
    const sev = options?.severities.find((s) => s.value === draft.severity)?.label ?? draft.severity
    return {
      condition, compName,
      durS,
      duration: durS > 0 ? `pendant au moins ${durHuman(draft.duration_value, draft.duration_unit)}` : 'dès la première observation',
      sev, who, chans,
      recovery: draft.auto_resolve
        ? `résolution automatique${draft.notify_recovery ? ` + message (${draft.channels_recovery.map((c) => options?.channels.find((x) => x.value === c)?.label ?? c).join(', ') || 'aucun canal'})` : ''}`
        : 'l’alerte reste ouverte (clôture manuelle)',
      cooldown: toSeconds(draft.cooldown_value, draft.cooldown_unit) > 0
        ? `réarmement ${durHuman(draft.cooldown_value, draft.cooldown_unit)}` : 'réarmement immédiat',
      /* LA phrase, pour le récapitulatif. */
      sentence: condition
        ? `Cette règle se déclenchera lorsque ${condition} ${durS > 0 ? `pendant au moins ${durHuman(draft.duration_value, draft.duration_unit)}` : ''}. Elle créera une alerte ${sev}${who.length > 0 ? ` et préviendra ${who.join(', ')} — par ${chans.join(', ')}` : ''}.`
        : null,
    }
  }, [service, metric, draft, options])

  if (!options || !loaded) {
    return <main className="lc-page lc-stagger"><p className="lc-hint">Chargement du catalogue…</p></main>
  }

  /* ═══════════ Contenu des étapes ═══════════ */

  const servicesFiltered = options.services.filter((s) =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.slug.includes(search.toLowerCase()))
  const byCat: { cat: string; items: ServiceOption[] }[] = []
  for (const s of servicesFiltered) {
    const last = byCat[byCat.length - 1]
    if (last && last.cat === s.category) last.items.push(s)
    else byCat.push({ cat: s.category, items: [s] })
  }

  const stepBodies: (() => ReactElement)[] = [
    /* ─── 1 · SOURCE ─── */
    () => (
      <>
        <label className="rb-search rw-search">
          <Search size={15} aria-hidden="true" />
          <input placeholder="Rechercher un service…" value={search} autoFocus
            onChange={(e) => setSearch(e.target.value)} />
        </label>
        <div className="rw-scrollable">
          {byCat.map((g) => (
            <div key={g.cat} className="rb-cat">
              <span className="lc-hint rb-cat__label">{g.cat}</span>
              <div className="rw-services">
                {g.items.map((s) => {
                  const I = iconOf(s.icon)
                  const on = draft.service_slug === s.slug
                  return (
                    <button key={s.slug} className={`rw-tile ${on ? 'rw-tile--on' : ''}`} aria-pressed={on}
                      onClick={() => { upd({ service_slug: s.slug, metric_key: null, component_key: null, operator: null, threshold: '' }); advance(1) }}>
                      <span className="rw-tile__icon"><I size={17} aria-hidden="true" /></span>
                      <span className="rw-tile__name">{s.name}</span>
                      {s.prominence === 'HIDDEN' ? <span className="lc-pill lc-pill--idle">Masqué</span> : null}
                      {!s.is_enabled ? <span className="lc-pill lc-pill--idle">Désactivé</span> : null}
                      {on ? <Check size={15} className="rw-tile__check" aria-hidden="true" /> : null}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
          {byCat.length === 0 ? <p className="lc-hint">Aucun service ne correspond à « {search} ».</p> : null}
        </div>
      </>
    ),
    /* ─── 2 · DONNÉE ─── */
    () => (
      <div className="rw-scrollable">
        <div className="rw-metrics" role="radiogroup" aria-label="Donnée">
          {(service?.metrics ?? []).map((m) => {
            const on = draft.metric_key === m.key
            return (
              <button key={m.key} role="radio" aria-checked={on}
                className={`rw-tile rw-tile--row ${on ? 'rw-tile--on' : ''}`}
                onClick={() => {
                  const p: Partial<Draft> = { metric_key: m.key, component_key: null, operator: m.operators.length === 1 ? m.operators[0]! : null, threshold: '' }
                  upd(p)
                  if (m.key !== 'component.status') advance(2)
                }}>
                <span className="lc-stack" style={{ gap: 2 }}>
                  <span className="rw-tile__name">{m.label}</span>
                  {isSuperAdmin ? <span className="lc-hint lc-mono">{m.technical_key}</span> : null}
                </span>
                {m.unit ? <span className="rw-tile__unit">{m.unit}</span> : null}
                {on ? <Check size={15} className="rw-tile__check" aria-hidden="true" /> : null}
              </button>
            )
          })}
        </div>
        {draft.metric_key === 'component.status' && metric?.components ? (
          <div style={{ marginTop: 14 }}>
            <Field label="Composant surveillé">
              <Select
                value={draft.component_key ?? ''}
                placeholder="Choisir un composant…"
                ariaLabel="Composant surveillé"
                options={metric.components.map((c) => ({ value: c.key, label: c.name }))}
                onChange={(v) => upd({ component_key: v || null })}
              />
            </Field>
          </div>
        ) : null}
      </div>
    ),
    /* ─── 3 · CONDITION — une phrase interactive : la lecture domine ─── */
    () => (
      <div className="rw-composer">
        <div className="rw-sentence" aria-label="Condition">
          <span className="rw-sentence__lead">Quand</span>
          <SentenceToken>{metric?.label ?? 'la donnée'}</SentenceToken>
          <Select
            className="rw-token" compact
            ariaLabel="Opérateur" placeholder="choisir…"
            value={draft.operator ?? ''}
            options={(metric?.operators ?? []).map((op) => ({ value: op, label: opLabel(metric?.type ?? '', op) }))}
            onChange={(v) => upd({ operator: v || null })}
          />
          {metric?.type === 'STATE' ? (
            <Select
              className="rw-token" compact
              ariaLabel="État" placeholder="choisir un état…"
              value={draft.threshold}
              options={(metric.enum_options ?? []).map((o) => ({ value: o.value, label: o.label, dot: STATE_DOTS[o.value] }))}
              onChange={(v) => upd({ threshold: v })}
            />
          ) : metric?.type === 'BOOLEAN' ? null : (
            <NumberInput
              ariaLabel="Valeur" width={160}
              value={draft.threshold}
              unit={metric ? (metric.type === 'AGE' ? 'minutes' : metric.unit) : null}
              onChange={(raw) => upd({ threshold: raw })}
            />
          )}
        </div>
        {stepDone[2] ? (
          <div className="rw-simulate">
            <button className="lc-btn" onClick={simulate}>
              <FlaskConical size={14} aria-hidden="true" /> Tester avec la valeur actuelle
            </button>
            {sim ? (
              <p className="rb-simulate__out lc-hint">
                Valeur actuelle : <strong>{sim.current_value}</strong> — {sim.verdict}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    ),
    /* ─── 4 · TEMPORISATION + réarmement ─── */
    () => (
      <>
        <fieldset className="rw-group">
          <legend>La condition doit rester vraie pendant…</legend>
          <div className="rb-chips">
            {DURATION_PRESETS.map((p) => {
              const on = draft.duration_value === p.v && (p.v === 0 || draft.duration_unit === p.u)
              return (
                <button key={p.label} className={`rb-chip ${on ? 'rb-chip--on' : ''}`} aria-pressed={on}
                  onClick={() => upd({ duration_value: p.v, duration_unit: p.u })}>{p.label}</button>
              )
            })}
          </div>
          <div className="rw-inline">
            <span className="lc-hint">ou précisément :</span>
            <NumberInput
              ariaLabel="Durée" steppers width={150} min={0}
              value={draft.duration_value}
              onChange={(raw) => upd({ duration_value: Math.max(0, Number(raw) || 0) })}
            />
            <Select
              compact ariaLabel="Unité de durée"
              value={draft.duration_unit}
              options={[{ value: 's', label: 'secondes' }, { value: 'min', label: 'minutes' }, { value: 'h', label: 'heures' }]}
              onChange={(v) => upd({ duration_unit: v as Draft['duration_unit'] })}
            />
          </div>
        </fieldset>

        <fieldset className="rw-group">
          <legend>Après une résolution, rester silencieuse pendant…</legend>
          <div className="rb-chips">
            {COOLDOWN_PRESETS.map((p) => {
              const on = draft.cooldown_value === p.v && (p.v === 0 || draft.cooldown_unit === p.u)
              return (
                <button key={p.label} className={`rb-chip ${on ? 'rb-chip--on' : ''}`} aria-pressed={on}
                  onClick={() => upd({ cooldown_value: p.v, cooldown_unit: p.u })}>{p.label}</button>
              )
            })}
          </div>
          <p className="lc-hint">Le réarmement évite le ping-pong « alerte / résolue / alerte ».</p>
        </fieldset>

        {metric && metric.type !== 'STATE' && metric.type !== 'BOOLEAN' ? (
          <fieldset className="rw-group">
            <SwitchRow
              checked={draft.advanced_recovery}
              onChange={(v) => upd({ advanced_recovery: v, recovery_operator: v ? draft.recovery_operator : null })}
              title="Seuil de rétablissement différent (avancé)"
              desc="Ex. déclenche > 90 %, ne se résout que sous 85 % — évite le flapping."
            />
            {draft.advanced_recovery ? (
              <div className="rw-inline" style={{ marginTop: 6 }}>
                <span className="lc-hint">Se résout quand la valeur est</span>
                <Select
                  compact ariaLabel="Opérateur de rétablissement" placeholder="choisir…"
                  value={draft.recovery_operator ?? ''}
                  options={['GT', 'GTE', 'LT', 'LTE', 'EQ', 'NEQ'].map((op) => ({ value: op, label: OP_LABELS[op]! }))}
                  onChange={(v) => upd({ recovery_operator: v || null })}
                />
                <NumberInput
                  ariaLabel="Valeur de rétablissement" width={150}
                  value={draft.recovery_threshold}
                  unit={metric.type === 'AGE' ? 'minutes' : metric.unit}
                  onChange={(raw) => upd({ recovery_threshold: raw })}
                />
              </div>
            ) : null}
          </fieldset>
        ) : null}
      </>
    ),
    /* ─── 5 · ALERTE ─── */
    () => (
      <>
        <div className="rw-field">
          <Field label="Nom de la règle" helper="C’est le titre que verront les personnes prévenues.">
            <TextInput value={draft.name} placeholder="Protect indisponible" maxLength={120} autoFocus
              onChange={(v) => upd({ name: v })} />
          </Field>
        </div>
        <fieldset className="rw-group">
          <legend>Sévérité</legend>
          <div className="rw-sev" role="radiogroup" aria-label="Sévérité">
            {options.severities.map((s) => {
              const on = draft.severity === s.value
              const meta = SEV_META[s.value] ?? { dot: 'lc-dot--info', desc: '' }
              return (
                <button key={s.value} role="radio" aria-checked={on}
                  className={`rw-tile rw-sev__card ${on ? 'rw-tile--on' : ''}`}
                  onClick={() => upd({ severity: s.value })}>
                  <span className={`lc-dot ${meta.dot}`} aria-hidden="true" />
                  <span className="lc-stack" style={{ gap: 2 }}>
                    <span className="rw-tile__name">{s.label}</span>
                    <span className="lc-hint">{meta.desc}</span>
                  </span>
                  {on ? <Check size={15} className="rw-tile__check" aria-hidden="true" /> : null}
                </button>
              )
            })}
          </div>
        </fieldset>
        <button className="rw-more" aria-expanded={showOverrides} onClick={() => setShowOverrides((s) => !s)}>
          <Pencil size={13} aria-hidden="true" /> Personnaliser les messages (facultatif)
        </button>
        {showOverrides ? (
          <div className="rw-overrides">
            <Field label="Description">
              <TextInput value={draft.description} maxLength={500} placeholder="À quoi sert cette règle ?"
                onChange={(v) => upd({ description: v })} />
            </Field>
            <Field label="Titre de l’alerte" helper="Vide : le modèle de message partagé s’applique.">
              <TextInput value={draft.title_override} placeholder="🔴 Protect indisponible" maxLength={200}
                onChange={(v) => upd({ title_override: v })} />
            </Field>
            <Field label="Sujet d’e-mail" helper={'Variables possibles : {{service.name}}, {{alert.title}}…'}>
              <TextInput value={draft.email_subject_override} placeholder={'⚠ {{service.name}}'} maxLength={200}
                onChange={(v) => upd({ email_subject_override: v })} />
            </Field>
          </div>
        ) : null}
      </>
    ),
    /* ─── 6 · DESTINATAIRES ─── */
    () => {
      const has = (kind: string, id: string) => draft.recipients.some((r) => r.kind === kind && r.id === id)
      const flip = (kind: 'USER' | 'ROLE' | 'GROUP', id: string) => upd({
        recipients: has(kind, id)
          ? draft.recipients.filter((r) => !(r.kind === kind && r.id === id))
          : [...draft.recipients, { kind, id }],
      })
      return (
        <div className="rw-scrollable">
          {options.recipients.groups.length > 0 ? (
            <fieldset className="rw-group">
              <legend>Groupes de notification</legend>
              <div className="rb-chips">
                {options.recipients.groups.map((g) => (
                  <button key={g.id} className={`rb-chip ${has('GROUP', g.id) ? 'rb-chip--on' : ''}`}
                    aria-pressed={has('GROUP', g.id)} onClick={() => flip('GROUP', g.id)}>
                    {g.name} <span className="lc-hint">({g.members})</span>
                  </button>
                ))}
              </div>
            </fieldset>
          ) : (
            <p className="lc-hint" style={{ marginBottom: 12 }}>
              Aucun groupe pour l’instant — créez-en un (ex. « Astreinte Technique ») dans
              Paramètres → Communications → Groupes : ses membres évolueront sans toucher aux règles.
            </p>
          )}
          <fieldset className="rw-group">
            <legend>Rôles</legend>
            <div className="rb-chips">
              {options.recipients.roles.map((r) => (
                <button key={r.key} className={`rb-chip ${has('ROLE', r.key) ? 'rb-chip--on' : ''}`}
                  aria-pressed={has('ROLE', r.key)} onClick={() => flip('ROLE', r.key)}>{r.label}</button>
              ))}
            </div>
          </fieldset>
          <fieldset className="rw-group">
            <legend>Personnes</legend>
            <div className="rb-chips">
              {options.recipients.users.map((u) => (
                <button key={u.id} className={`rb-chip ${has('USER', u.id) ? 'rb-chip--on' : ''}`}
                  aria-pressed={has('USER', u.id)} onClick={() => flip('USER', u.id)}>{u.display_name}</button>
              ))}
            </div>
          </fieldset>
        </div>
      )
    },
    /* ─── 7 · COMMUNICATIONS — deux vraies cartes, canaux en cards ─── */
    () => {
      const flip = (list: 'channels_fire' | 'channels_recovery', value: string, on: boolean) => upd({
        [list]: on ? [...draft[list], value] : draft[list].filter((x) => x !== value),
      } as Partial<Draft>)
      return (
        <div className="rw-comm">
          <div className="rw-comm__card">
            <span className="rw-comm__head"><Megaphone size={15} aria-hidden="true" /> Au déclenchement</span>
            <span className="rw-comm__q">Comment prévenir ?</span>
            <div className="lc-chancards">
              {options.channels.map((c) => (
                <ChannelCard key={c.value}
                  checked={draft.channels_fire.includes(c.value)}
                  onChange={(on) => flip('channels_fire', c.value, on)}
                  icon={CHANNEL_ICONS[c.value] ?? <Bell size={17} />}
                  label={c.label}
                  note={c.value === 'PUSH' ? 'Préparé · mobile à venir' : null}
                />
              ))}
            </div>
            <span className="lc-helper">
              La politique d’organisation et les préférences personnelles s’appliquent ensuite.
            </span>
          </div>
          <div className="rw-comm__card">
            <span className="rw-comm__head"><Undo2 size={15} aria-hidden="true" /> Au rétablissement</span>
            <SwitchRow
              checked={draft.auto_resolve}
              onChange={(v) => upd({ auto_resolve: v })}
              title="Résoudre automatiquement"
              desc="Recommandé. Sinon, clôture manuelle depuis l’alerte."
            />
            <SwitchRow
              checked={draft.notify_recovery}
              onChange={(v) => upd({ notify_recovery: v })}
              title="Envoyer un message de rétablissement"
            />
            {draft.notify_recovery ? (
              <div className="lc-chancards">
                {options.channels.map((c) => (
                  <ChannelCard key={c.value}
                    checked={draft.channels_recovery.includes(c.value)}
                    onChange={(on) => flip('channels_recovery', c.value, on)}
                    icon={CHANNEL_ICONS[c.value] ?? <Bell size={17} />}
                    label={c.label}
                    note={c.value === 'PUSH' ? 'Préparé · mobile à venir' : null}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )
    },
    /* ─── 8 · RÉCAPITULATIF ─── */
    () => (
      <div className="rw-scrollable">
        {summary.sentence ? (
          <div className="rw-recap-hero">
            <BellRing size={17} aria-hidden="true" />
            <p>{summary.sentence}</p>
          </div>
        ) : (
          <p className="lc-hint">Le récapitulatif se complètera quand les étapes précédentes seront remplies.</p>
        )}
        <div className="rw-recap">
          {[
            { i: 0, k: 'Service', v: service ? service.name : null },
            { i: 1, k: 'Donnée', v: metric ? `${metric.label}${summary.compName ? ` — ${summary.compName}` : ''}` : null },
            { i: 2, k: 'Condition', v: summary.condition ? `${opLabel(metric?.type ?? '', draft.operator ?? '')}${metric?.type === 'BOOLEAN' ? '' : ` ${metric?.type === 'STATE' ? metric.enum_options?.find((o) => o.value === draft.threshold)?.label ?? draft.threshold : draft.threshold}${metric?.type === 'AGE' ? ' min' : metric?.unit ? ` ${metric.unit}` : ''}`}` : null },
            { i: 3, k: 'Temporisation', v: summary.duration },
            { i: 3, k: 'Réarmement', v: summary.cooldown.replace('réarmement ', '') },
            { i: 4, k: 'Sévérité', v: draft.name ? `${summary.sev} — « ${draft.name} »` : null },
            { i: 5, k: 'Destinataires', v: summary.who.length > 0 ? summary.who.join(', ') : null },
            { i: 6, k: 'Déclenchement', v: summary.chans.length > 0 ? summary.chans.join(' · ') : null },
            { i: 6, k: 'Rétablissement', v: summary.recovery },
          ].map((row, n) => (
            <div key={n} className="rw-recap__row">
              <span className="rw-recap__k">{row.k}</span>
              <span className={`rw-recap__v ${row.v ? '' : 'rw-recap__v--missing'}`}>{row.v ?? 'à définir'}</span>
              <button className="lc-btn lc-btn--ghost rw-recap__edit" aria-label={`Modifier : ${row.k}`}
                onClick={() => goto(row.i)}><Pencil size={12} aria-hidden="true" /></button>
            </div>
          ))}
        </div>
        <p className="lc-hint" style={{ marginTop: 12 }}>
          {allValid
            ? (ruleId ? 'Enregistrez vos modifications avec la barre ci-dessous.' : 'La règle sera active dès sa création — utilisez la barre ci-dessous.')
            : `Il reste quelque chose : ${stepIssue[firstIssue]}`}
        </p>
      </div>
    ),
  ]

  /* ═══════════ Résumé vivant (panneau latéral) — live preview ═══════════ */
  const compLabel = draft.metric_key === 'component.status'
    ? metric?.components?.find((c) => c.key === draft.component_key)?.name ?? null : null
  const condToken = metric && draft.operator
    ? `${opLabel(metric.type, draft.operator)}${metric.type === 'BOOLEAN' ? ''
        : ` ${metric.type === 'STATE'
          ? metric.enum_options?.find((o) => o.value === draft.threshold)?.label ?? (draft.threshold || '…')
          : `${draft.threshold || '…'}${metric.type === 'AGE' ? ' min' : metric.unit ? ` ${metric.unit}` : ''}`}`}`
    : null
  const summaryPanel = (
    <aside className="rw-summary" aria-label="Votre règle, en français" aria-live="polite">
      <h2 className="rw-summary__title"><BellRing size={14} aria-hidden="true" /> Votre règle</h2>
      <div className="rw-summary__block">
        <span className="rw-summary__label">Déclencheur</span>
        {service && metric ? (
          <>
            <div className="rw-summary__tokens">
              <span className="rw-summary__token">{service.name}</span>
              {compLabel ? <span className="rw-summary__token">{compLabel}</span> : null}
              <span className="rw-summary__token">{metric.label}</span>
              {condToken ? <span className="rw-summary__token">{condToken}</span> : null}
            </div>
            <p className="rw-summary__line rw-summary__soft2" style={{ marginTop: 6 }}>{summary.duration}</p>
          </>
        ) : <p className="rw-summary__placeholder">choisissez un service, une donnée et une condition…</p>}
      </div>
      <div className="rw-summary__block">
        <span className="rw-summary__label">Alerte</span>
        <p className="rw-summary__line">
          <span className="rw-summary__pill">
            <span className={`lc-dot ${SEV_META[draft.severity]?.dot ?? ''}`} aria-hidden="true" /> {summary.sev}
          </span>
          {draft.name ? <span>« {draft.name} »</span> : null}
        </p>
      </div>
      <div className="rw-summary__block">
        <span className="rw-summary__label">Destinataires</span>
        {summary.who.length > 0
          ? <div className="rw-summary__tokens">{summary.who.map((w) => <span key={w} className="rw-summary__token">{w}</span>)}</div>
          : <p className="rw-summary__placeholder">personne pour l’instant…</p>}
      </div>
      <div className="rw-summary__block">
        <span className="rw-summary__label">Canaux</span>
        {summary.chans.length > 0
          ? <div className="rw-summary__tokens">{summary.chans.map((c) => <span key={c} className="rw-summary__token">{c}</span>)}</div>
          : <p className="rw-summary__placeholder">aucun canal…</p>}
      </div>
      <div className="rw-summary__block">
        <span className="rw-summary__label">Rétablissement</span>
        <p className="rw-summary__line rw-summary__soft2">{summary.recovery}</p>
        <p className="rw-summary__line rw-summary__soft2">{summary.cooldown}</p>
      </div>
    </aside>
  )

  const issue = stepIssue[step]
  const isLast = step === STEPS.length - 1

  return (
    <main className="lc-page lc-stagger rw-page">
      <div className="rw-top">
        <button className="lc-btn lc-btn--ghost" onClick={() => navigate('/alerts/rules')}>
          <ChevronLeft size={14} aria-hidden="true" /> Règles
        </button>
        <div>
          <h1 className="lc-title" style={{ marginBottom: 2 }}>{ruleId ? 'Modifier la règle' : 'Nouvelle règle'}</h1>
          <p className="lc-subtitle" style={{ margin: 0 }}>Étape {step + 1} sur {STEPS.length} — {STEPS[step]!.label}</p>
        </div>
      </div>

      {/* Progression mobile (le rail est masqué en étroit) */}
      <div className="rb-progress rw-progress-mobile" aria-hidden="true">
        {STEPS.map((_, i) => <span key={i} className={`rb-progress__dot ${i <= step ? 'rb-progress__dot--on' : ''}`} />)}
      </div>

      <div className="rw-layout">
        {/* ─── Rail de progression (desktop) ─── */}
        <nav className="rw-rail" aria-label="Étapes">
          {STEPS.map((s, i) => {
            const state = i === step ? 'current' : stepDone[i] && i < step + 1 && (i <= visited || i < step) ? 'done' : reachable(i) ? 'open' : 'locked'
            const valueHint =
              i === 0 && service ? service.name
              : i === 1 && metric ? metric.label
              : i === 2 && condToken ? condToken
              : i === 3 ? (summary.durS > 0 ? durHuman(draft.duration_value, draft.duration_unit) : 'immédiat')
              : i === 4 && draft.name ? summary.sev
              : i === 5 && summary.who.length > 0 ? `${summary.who.length} destinataire${summary.who.length > 1 ? 's' : ''}`
              : i === 6 && summary.chans.length > 0 ? summary.chans.join(' · ')
              : null
            return (
              <button key={s.key} className={`rw-rail__item rw-rail__item--${state}`}
                aria-current={i === step ? 'step' : undefined}
                disabled={state === 'locked'}
                onClick={() => goto(i)}>
                <span className="rw-rail__num">{stepDone[i] && i !== step ? <Check size={13} aria-hidden="true" /> : i + 1}</span>
                <span className="rw-rail__text">
                  <span className="rw-rail__label">{s.label}</span>
                  {valueHint && i !== step ? <span className="rw-rail__value">{valueHint}</span> : null}
                </span>
              </button>
            )
          })}
        </nav>

        {/* ─── LA carte d'étape ─── */}
        <section className="rw-card" aria-label={STEPS[step]!.label}>
          <header className="rw-card__head">
            <h2>{STEPS[step]!.ask}</h2>
            {STEPS[step]!.hint ? <p className="lc-hint">{STEPS[step]!.hint}</p> : null}
          </header>
          <div className={`rw-card__body${step === 2 ? ' rw-card__body--center' : ''}`}>{stepBodies[step]!()}</div>
          <footer className="rw-card__nav">
            <button className="lc-btn" disabled={step === 0} onClick={() => goto(step - 1)}>
              <ArrowLeft size={14} aria-hidden="true" /> Précédent
            </button>
            <span className="rw-card__issue" role="status">{issue ?? ''}</span>
            {!isLast ? (
              <button className="lc-btn lc-btn--primary" disabled={issue !== null} onClick={() => goto(step + 1)}>
                Suivant <ArrowRight size={14} aria-hidden="true" />
              </button>
            ) : <span />}
          </footer>
        </section>

        {summaryPanel}
      </div>
    </main>
  )
}

export function RuleBuilderPage({ ruleId }: { ruleId: string | null }) {
  const meta = useShellMeta()
  return (
    <ToastProvider>
      <Shell active="alerts" meta={meta}>
        <Inner ruleId={ruleId} />
      </Shell>
    </ToastProvider>
  )
}
